import React, { useState } from 'react';
import { Form, Input, Checkbox, Popup, Icon, Button } from 'semantic-ui-react';
import { NumericFormat, NumberFormatValues } from 'react-number-format';

type GrantValues = {
    id: string;
    amount?: number | null;
    availableForConstruction?: boolean;
};

type GrantsValues = {
    grants: GrantValues[];
};

type Props = {
    values?: GrantsValues;
    onChange: (values: GrantsValues) => void;
};

const createDefaultGrant = (): GrantValues => ({
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    amount: null,
    availableForConstruction: false,
});

const Grants: React.FC<Props> = ({
    values = { grants: [] },
    onChange,
}) => {
    const [formValues, setFormValues] = useState<GrantsValues>({
        grants: values.grants.length > 0 ? values.grants : [],
    });

    const addGrant = () => {
        const newGrant = createDefaultGrant();
        const updatedValues = {
            grants: [...formValues.grants, newGrant]
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const deleteGrant = (grantId: string) => {
        const updatedValues = {
            grants: formValues.grants.filter(grant => grant.id !== grantId)
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const handleChange = (
        grantId: string,
        name: string,
        value: any
    ) => {
        const newValue = value === '' ? null : Number(value);
        const updatedValues = {
            grants: formValues.grants.map(grant =>
                grant.id === grantId
                    ? { ...grant, [name]: newValue }
                    : grant
            )
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const handleCheckboxChange = (
        grantId: string,
        _e: React.FormEvent<HTMLInputElement>,
        data: { name?: string; checked?: boolean }
    ) => {
        const { name, checked } = data;
        if (name === undefined) return;
        const updatedValues = {
            grants: formValues.grants.map(grant =>
                grant.id === grantId
                    ? { ...grant, [name]: checked }
                    : grant
            )
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const renderGrantFields = (grant: GrantValues, index: number) => (
        <div key={grant.id} style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <h3>Grant {index + 1}</h3>

            <Form.Field>
                <label>Grant amount</label>
                <NumericFormat
                    thousandSeparator
                    allowNegative={false}
                    customInput={Input}
                    name="amount"
                    value={grant.amount ?? ''}
                    onValueChange={(values: NumberFormatValues) =>
                        handleChange(grant.id, 'amount', values.value)
                    }
                    placeholder="Enter amount..."
                    label={{ basic: true, content: '$' }}
                    labelPosition="right"
                />
            </Form.Field>
            <Form.Field>
                <label>Available for construction?</label>
                <Checkbox
                    key={grant.id + '_availableForConstruction'}
                    name="availableForConstruction"
                    checked={grant.availableForConstruction === true}
                    toggle
                    onChange={(e, data) => handleCheckboxChange(grant.id, e, data)}
                    label={grant.availableForConstruction ? 'Yes' : 'No'}
                />
            </Form.Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #ddd', paddingTop: '16px' }}>
                <Button
                    basic
                    color="red"
                    size="small"
                    onClick={() => deleteGrant(grant.id)}
                    title="Delete grant"
                >Remove</Button>
            </div>
        </div>
    );

    return (
        <Form>
            <h4>Grants</h4>

            {formValues.grants.map((grant, index) => renderGrantFields(grant, index))}

            <Button
                secondary
                type="button"
                icon="plus"
                content="Add grant"
                fluid
                onClick={addGrant}
                style={{ marginBottom: '24px', padding: '16px', textAlign: 'center', boxShadow: 'none' }}
            />
        </Form>
    );
};

export default Grants;